import { TrackShapeSettings } from '../core/geometry/gearRack';
import { type2Layout, type2Skeleton, Type2ShapeParams } from '../core/geometry/type2Shape';

const SVG_NS = 'http://www.w3.org/2000/svg';
const DIM_COLOR = '#7eb8ff';
/** Part names, kept a different colour from the blue dimensions. */
const PART_COLOR = '#ffc46b';
const M_TO_MM = 1000;

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** One part name: the point it points at, and where its row wants to sit. */
interface Callout {
  text: string;
  /** Point on the part where the leader line lands. */
  ax: number;
  ay: number;
  /** Preferred label row; rows get pushed down when they would collide. */
  y: number;
}

function mm(metres: number): string {
  const v = metres * M_TO_MM;
  const rounded = Math.abs(v - Math.round(v)) < 1e-6 ? String(Math.round(v)) : v.toFixed(1);
  return `${rounded} mm`;
}

function svgEl<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

function addHDim(
  parent: SVGElement,
  x1: number,
  x2: number,
  dimY: number,
  fromY1: number,
  fromY2: number,
  text: string,
  fontSize: number,
): void {
  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);
  const over = fontSize * 0.35;
  const dir1 = dimY < fromY1 ? -1 : 1;
  parent.appendChild(
    svgEl('line', {
      x1: String(x1),
      y1: String(fromY1),
      x2: String(x1),
      y2: String(dimY + dir1 * over),
      stroke: DIM_COLOR,
      'stroke-width': String(Math.max(0.6, fontSize * 0.08)),
      'stroke-dasharray': '3 3',
      fill: 'none',
    }),
  );
  parent.appendChild(
    svgEl('line', {
      x1: String(x2),
      y1: String(fromY2),
      x2: String(x2),
      y2: String(dimY + (dimY < fromY2 ? -1 : 1) * over),
      stroke: DIM_COLOR,
      'stroke-width': String(Math.max(0.6, fontSize * 0.08)),
      'stroke-dasharray': '3 3',
      fill: 'none',
    }),
  );
  const dim = svgEl('line', {
    x1: String(left),
    y1: String(dimY),
    x2: String(right),
    y2: String(dimY),
    stroke: DIM_COLOR,
    'stroke-width': String(Math.max(0.7, fontSize * 0.1)),
    fill: 'none',
    'marker-start': 'url(#track-dim-arrow-start)',
    'marker-end': 'url(#track-dim-arrow)',
  });
  parent.appendChild(dim);
  const labelX = right - left < fontSize * 2.4 ? right + fontSize * 1.4 : (left + right) / 2;
  const labelY = right - left < fontSize * 2.4 ? dimY : dimY + dir1 * -fontSize * 0.95;
  addLabel(parent, labelX, labelY, text, fontSize);
}

function addVDim(
  parent: SVGElement,
  y1: number,
  y2: number,
  dimX: number,
  fromX1: number,
  fromX2: number,
  text: string,
  fontSize: number,
): void {
  const top = Math.min(y1, y2);
  const bot = Math.max(y1, y2);
  const over = fontSize * 0.35;
  const dir1 = dimX < fromX1 ? -1 : 1;
  parent.appendChild(
    svgEl('line', {
      x1: String(fromX1),
      y1: String(y1),
      x2: String(dimX + dir1 * over),
      y2: String(y1),
      stroke: DIM_COLOR,
      'stroke-width': String(Math.max(0.6, fontSize * 0.08)),
      'stroke-dasharray': '3 3',
      fill: 'none',
    }),
  );
  parent.appendChild(
    svgEl('line', {
      x1: String(fromX2),
      y1: String(y2),
      x2: String(dimX + (dimX < fromX2 ? -1 : 1) * over),
      y2: String(y2),
      stroke: DIM_COLOR,
      'stroke-width': String(Math.max(0.6, fontSize * 0.08)),
      'stroke-dasharray': '3 3',
      fill: 'none',
    }),
  );
  parent.appendChild(
    svgEl('line', {
      x1: String(dimX),
      y1: String(top),
      x2: String(dimX),
      y2: String(bot),
      stroke: DIM_COLOR,
      'stroke-width': String(Math.max(0.7, fontSize * 0.1)),
      fill: 'none',
      'marker-start': 'url(#track-dim-arrow-start)',
      'marker-end': 'url(#track-dim-arrow)',
    }),
  );
  const labelX = bot - top < fontSize * 2 ? dimX : dimX + dir1 * fontSize * 1.25;
  const labelY = bot - top < fontSize * 2 ? top - fontSize : (top + bot) / 2;
  addLabel(parent, labelX, labelY, text, fontSize);
}

/** Text on a dark plate. `anchor: 'end'` right-aligns it on `x`. Returns its box. */
function addLabel(
  parent: SVGElement,
  x: number,
  y: number,
  text: string,
  fontSize: number,
  color: string = DIM_COLOR,
  anchor: 'middle' | 'end' = 'middle',
): Bounds {
  const el = svgEl('text', {
    x: String(x),
    y: String(y),
    fill: color,
    'font-size': String(fontSize),
    'font-family': 'Segoe UI, system-ui, sans-serif',
    'text-anchor': anchor,
    'dominant-baseline': 'middle',
  });
  el.textContent = text;
  parent.appendChild(el);
  // A hidden panel measures as zero — fall back to an estimate so the plate and
  // the view box still roughly fit the text.
  const w = Math.max(fontSize, el.getComputedTextLength() || text.length * fontSize * 0.55);
  const left = (anchor === 'end' ? x - w : x - w / 2) - fontSize * 0.2;
  const top = y - fontSize * 0.65;
  const bg = svgEl('rect', {
    x: String(left),
    y: String(top),
    width: String(w + fontSize * 0.4),
    height: String(fontSize * 1.25),
    rx: String(fontSize * 0.15),
    fill: '#0b0e12',
  });
  parent.insertBefore(bg, el);
  return { minX: left, minY: top, maxX: left + w + fontSize * 0.4, maxY: top + fontSize * 1.25 };
}

/** Push rows (given top to bottom) apart so a stacked name column never overlaps. */
function stackRows(ys: number[], minGap: number): number[] {
  const out = ys.slice();
  for (let i = 1; i < out.length; i++) {
    if (out[i] - out[i - 1] < minGap) out[i] = out[i - 1] + minGap;
  }
  return out;
}

function unionBounds(a: Bounds | null, b: Bounds): Bounds {
  if (!a) return b;
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

function padBounds(box: Bounds, extra: Bounds | null, margin: number): Bounds {
  if (!extra) return box;
  return {
    minX: Math.min(box.minX, extra.minX - margin),
    minY: Math.min(box.minY, extra.minY - margin),
    maxX: Math.max(box.maxX, extra.maxX + margin),
    maxY: Math.max(box.maxY, extra.maxY + margin),
  };
}

/**
 * Part names as a right-aligned column of leader-line callouts, each dot sitting
 * on the part it names. Returns the box the names occupy, so the caller can grow
 * the view box to fit them.
 */
function drawCallouts(
  parent: SVGElement,
  colX: number,
  callouts: Callout[],
  fontSize: number,
): Bounds | null {
  const rows = stackRows(
    callouts.map((c) => c.y),
    fontSize * 1.5,
  );
  let bounds: Bounds | null = null;
  for (let i = 0; i < callouts.length; i++) {
    const c = callouts[i];
    const y = rows[i];
    parent.appendChild(
      svgEl('line', {
        x1: String(colX + fontSize * 0.35),
        y1: String(y),
        x2: String(c.ax),
        y2: String(c.ay),
        stroke: PART_COLOR,
        'stroke-width': String(Math.max(0.6, fontSize * 0.07)),
        fill: 'none',
      }),
    );
    parent.appendChild(
      svgEl('circle', {
        cx: String(c.ax),
        cy: String(c.ay),
        r: String(Math.max(1, fontSize * 0.14)),
        fill: PART_COLOR,
      }),
    );
    bounds = unionBounds(bounds, addLabel(parent, colX, y, c.text, fontSize, PART_COLOR, 'end'));
  }
  return bounds;
}

function ensureMarkers(svg: SVGSVGElement): void {
  let defs = svg.querySelector('defs');
  if (!defs) {
    defs = svgEl('defs', {});
    svg.appendChild(defs);
  }
  if (!svg.querySelector('#track-dim-arrow')) {
    const end = svgEl('marker', {
      id: 'track-dim-arrow',
      markerWidth: '8',
      markerHeight: '8',
      refX: '7',
      refY: '4',
      orient: 'auto',
    });
    end.appendChild(svgEl('path', { d: 'M0 0 L8 4 L0 8 z', fill: DIM_COLOR }));
    const start = svgEl('marker', {
      id: 'track-dim-arrow-start',
      markerWidth: '8',
      markerHeight: '8',
      refX: '1',
      refY: '4',
      orient: 'auto',
    });
    start.appendChild(svgEl('path', { d: 'M8 0 L0 4 L8 8 z', fill: DIM_COLOR }));
    defs.appendChild(start);
    defs.appendChild(end);
  }
}

function setViewBox(svg: SVGSVGElement, box: Bounds): void {
  const w = Math.max(1, box.maxX - box.minX);
  const h = Math.max(1, box.maxY - box.minY);
  svg.setAttribute('viewBox', `${box.minX} ${box.minY} ${w} ${h}`);
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
}

function drawType1(svg: SVGSVGElement, shape: TrackShapeSettings): void {
  const g = shape.railSpacing * M_TO_MM;
  const r = (shape.railDiameter / 2) * M_TO_MM;
  const flatH = shape.flatBeamHeight * M_TO_MM;
  const triW = shape.triangleWidth * M_TO_MM;
  const triH = shape.triangleHeight * M_TO_MM;
  const deckBottom = flatH;
  const apexY = deckBottom + triH;
  const halfG = g / 2;
  const halfT = triW / 2;
  const span = Math.max(g + r * 2, flatH + triH + r);
  const fontSize = Math.max(10, span * 0.045);
  const dim = Math.max(28, span * 0.1);

  const geom = svgEl('g', { 'stroke-linejoin': 'round' });
  geom.appendChild(
    svgEl('rect', {
      x: String(-halfG),
      y: '0',
      width: String(g),
      height: String(Math.max(flatH, 0.5)),
      fill: '#e6c84a',
      stroke: 'none',
    }),
  );
  geom.appendChild(
    svgEl('polygon', {
      points: `${-halfT},${deckBottom} 0,${apexY} ${halfT},${deckBottom}`,
      fill: '#4a8fe7',
      stroke: 'none',
    }),
  );
  for (const cx of [-halfG, halfG]) {
    geom.appendChild(
      svgEl('circle', {
        cx: String(cx),
        cy: '0',
        r: String(r),
        fill: '#1a1d22',
        stroke: '#f2f3f5',
        'stroke-width': String(Math.max(1, r * 0.12)),
      }),
    );
  }
  svg.appendChild(geom);

  const dims = svgEl('g', {});
  svg.appendChild(dims);
  if (shape.railSpacing > 0) {
    addHDim(dims, -halfG, halfG, -r - dim, 0, 0, mm(shape.railSpacing), fontSize);
  }
  if (shape.railDiameter > 0) {
    addVDim(dims, -r, r, -halfG - r - dim, -halfG, -halfG, mm(shape.railDiameter), fontSize);
  }
  if (shape.flatBeamHeight > 0) {
    addVDim(dims, 0, deckBottom, halfG + r + dim, halfG, halfG, mm(shape.flatBeamHeight), fontSize);
  }
  if (shape.triangleWidth > 0) {
    addHDim(dims, -halfT, halfT, apexY + dim, deckBottom, deckBottom, mm(shape.triangleWidth), fontSize);
  }
  if (shape.triangleHeight > 0) {
    addVDim(dims, deckBottom, apexY, halfG + r + dim * 1.85, halfT, 0, mm(shape.triangleHeight), fontSize);
  }

  // Part names go down the left: that side only carries the rail-diameter
  // dimension, so the rows only have to dodge its label at y = 0.
  const parts = svgEl('g', {});
  svg.appendChild(parts);
  const partFont = Math.max(9, fontSize * 0.9);
  const colX = -halfG - r - dim * 1.15;
  const callouts: Callout[] = [
    {
      text: 'Track beam',
      ax: -halfG - r * 0.71,
      ay: -r * 0.71,
      y: -r - Math.max(dim * 0.9, partFont * 1.45),
    },
  ];
  if (shape.flatBeamHeight > 0) {
    callouts.push({
      // Bottom edge, clear of the rail, so the leader passes under the circle.
      text: 'Flat beam',
      ax: -halfG + Math.min(r * 1.25, g * 0.3),
      ay: deckBottom,
      y: Math.max(deckBottom + dim * 0.5, r + partFont, partFont * 1.45),
    });
  }
  if (shape.triangleHeight > 0 && shape.triangleWidth > 0) {
    callouts.push({
      text: 'Triangle',
      ax: -halfT * 0.5,
      ay: deckBottom + triH * 0.5,
      y: deckBottom + triH * 0.62,
    });
  }
  const partBounds = drawCallouts(parts, colX, callouts, partFont);

  const pad = dim * 1.6 + fontSize;
  setViewBox(
    svg,
    padBounds(
      {
        minX: -halfG - r - dim - pad,
        minY: -r - dim - pad,
        maxX: halfG + r + dim * 2.2 + pad,
        maxY: apexY + dim + pad,
      },
      partBounds,
      partFont * 0.6,
    ),
  );
}

function drawType2(svg: SVGSVGElement, shape: TrackShapeSettings): void {
  const params: Type2ShapeParams = {
    topGap: shape.type2TopGap,
    bottomWidth: shape.type2BottomWidth,
    vHeight: shape.type2VHeight,
    rackTopWidth: shape.type2RackTopWidth,
    rackTopHeight: shape.type2RackTopHeight,
    rackBottomHeight: shape.type2RackBottomHeight,
    beamHeight: shape.type2BeamHeight,
    circleDiameter: shape.type2CircleDiameter,
    thickness: shape.type2Thickness,
  };
  const L = type2Layout(params);
  const s = M_TO_MM;
  const leftTopX = L.leftTopX * s;
  const rightTopX = L.rightTopX * s;
  const leftX = L.leftX * s;
  const rightX = L.rightX * s;
  const rackTopLeftX = L.rackTopLeftX * s;
  const rackTopRightX = L.rackTopRightX * s;
  const topY = -L.topY * s;
  const vBottomY = -L.vBottomY * s;
  const rackTopBottomY = -L.rackTopBottomY * s;
  const rackBottomY = -L.rackBottomY * s;
  const beamBottomY = -L.beamBottomY * s;
  const r = L.r * s;
  const t = L.thickness * s;
  const span = Math.max(rightTopX - leftTopX + r * 2, beamBottomY - topY + r * 2, 80);
  const fontSize = Math.max(10, span * 0.042);
  const dim = Math.max(28, span * 0.09);
  const halfT = t / 2;

  // Same skeleton the mesh sweeps, so preview and 3D track cannot drift apart.
  // Butt caps + mitre joins: pointy corners, no rounding, like the mesh.
  const solidWebs = shape.type2SolidWebs;
  const sk = type2Skeleton(params, !solidWebs);
  const geom = svgEl('g', {
    stroke: '#f4f7fb',
    'stroke-width': String(t),
    'stroke-linecap': 'butt',
    'stroke-linejoin': 'miter',
    'stroke-miterlimit': '4',
  });
  for (const fill of sk.fills) {
    geom.appendChild(
      svgEl('polygon', {
        points: fill.map((pt) => `${pt.x * s},${-pt.y * s}`).join(' '),
        fill: '#f4f7fb',
        stroke: 'none',
      }),
    );
  }
  // Open sides are only there at each plate, so draw them washed out and dashed —
  // the section is still solid where a plate sits.
  for (const web of sk.webs) {
    geom.appendChild(
      svgEl('polygon', {
        points: web.map((pt) => `${pt.x * s},${-pt.y * s}`).join(' '),
        fill: '#f4f7fb',
        'fill-opacity': solidWebs ? '1' : '0.3',
        stroke: solidWebs ? 'none' : '#f4f7fb',
        'stroke-width': String(solidWebs ? 0 : Math.max(0.8, t * 0.25)),
        'stroke-dasharray': solidWebs ? 'none' : `${t * 0.7} ${t * 0.7}`,
        'stroke-linecap': 'butt',
      }),
    );
  }
  for (const bar of sk.bars) {
    geom.appendChild(
      svgEl('path', {
        d: bar.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x * s} ${-pt.y * s}`).join(' '),
        fill: 'none',
      }),
    );
  }
  // V arms and inner walls ride on the plates too, so they wash out with them.
  for (const bar of sk.webBars) {
    geom.appendChild(
      svgEl('path', {
        d: bar.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x * s} ${-pt.y * s}`).join(' '),
        fill: 'none',
        'stroke-opacity': '0.3',
      }),
    );
  }
  for (const rail of sk.rails) {
    geom.appendChild(
      svgEl('circle', {
        cx: String(rail.x * s),
        cy: String(-rail.y * s),
        r: String(rail.r * s),
        fill: '#f4f7fb',
        stroke: 'none',
      }),
    );
  }
  svg.appendChild(geom);

  const dims = svgEl('g', {});
  svg.appendChild(dims);
  const geomLeft = Math.min(leftTopX - r, leftX, rackTopLeftX) - halfT;
  const geomRight = Math.max(rightTopX + r, rightX, rackTopRightX) + halfT;
  const geomTop = topY - r;
  const geomBottom = beamBottomY + halfT;
  const xRight = geomRight + dim;
  const xLeft = geomLeft - dim;
  const yTop = geomTop - dim;
  const yBottom = geomBottom + dim;

  if (shape.type2TopGap > 0) addHDim(dims, leftTopX, rightTopX, yTop, topY, topY, mm(shape.type2TopGap), fontSize);
  if (shape.type2BottomWidth > 0) {
    addHDim(dims, leftX, rightX, yBottom, beamBottomY, beamBottomY, mm(shape.type2BottomWidth), fontSize);
  }
  // Only worth a dimension once the rack top stops being flush with the stem.
  if (Math.abs(rackTopLeftX - leftX) > 1e-6) {
    addHDim(
      dims,
      rackTopLeftX,
      rackTopRightX,
      vBottomY - dim * 0.75,
      vBottomY,
      vBottomY,
      mm(L.rackTopRightX - L.rackTopLeftX),
      fontSize,
    );
  }
  if (shape.type2CircleDiameter > 0) {
    addVDim(dims, topY - r, topY + r, xLeft, leftTopX, leftTopX, mm(shape.type2CircleDiameter), fontSize);
  }
  if (shape.type2VHeight > 0) {
    addVDim(dims, topY, vBottomY, xRight, rightTopX, rightX, mm(shape.type2VHeight), fontSize);
  }
  if (shape.type2RackTopHeight > 0) {
    addVDim(dims, vBottomY, rackTopBottomY, xRight, rightX, rightX, mm(shape.type2RackTopHeight), fontSize);
  }
  if (shape.type2RackBottomHeight > 0) {
    addVDim(dims, rackTopBottomY, rackBottomY, xRight, rightX, rightX, mm(shape.type2RackBottomHeight), fontSize);
  }
  if (shape.type2BeamHeight > 0) {
    addVDim(dims, rackBottomY, beamBottomY, xRight, rightX, rightX, mm(shape.type2BeamHeight), fontSize);
  }
  if (shape.type2Thickness > 0) {
    addHDim(
      dims,
      rightX - halfT,
      rightX + halfT,
      yBottom + dim,
      beamBottomY,
      beamBottomY,
      mm(shape.type2Thickness),
      fontSize,
    );
  }

  // All four height dimensions live on the right, so the names take the left.
  const parts = svgEl('g', {});
  svg.appendChild(parts);
  const partFont = Math.max(9, fontSize * 0.9);
  const colX = geomLeft - dim * 0.35;
  const callouts: Callout[] = [
    {
      text: 'Rail circle',
      ax: leftTopX - r * 0.71,
      ay: topY - r * 0.71,
      y: topY - r - Math.max(dim * 0.9, partFont * 1.45),
    },
  ];
  // Point at the middle of the web itself: it survives a zero-height V, where the
  // arms flatten out but the side is still there.
  const leftWeb = sk.webs[0];
  if (leftWeb && leftWeb.length > 0) {
    const wx = (leftWeb.reduce((a, p) => a + p.x, 0) / leftWeb.length) * s;
    const wy = -(leftWeb.reduce((a, p) => a + p.y, 0) / leftWeb.length) * s;
    callouts.push({
      text: solidWebs ? 'V side' : 'V plates',
      ax: wx,
      ay: wy,
      y: Math.max(wy, partFont * 1.45),
    });
  }
  // Rack and beam names land on the outer left face, where nothing is in the way.
  if (shape.type2RackTopHeight > 0) {
    callouts.push({
      text: 'Rack top',
      ax: (rackTopLeftX + leftX) / 2 - halfT,
      ay: (vBottomY + rackTopBottomY) / 2,
      y: (vBottomY + rackTopBottomY) / 2,
    });
  }
  if (shape.type2RackBottomHeight > 0) {
    callouts.push({
      text: 'Rack bottom',
      ax: leftX - halfT,
      ay: (rackTopBottomY + rackBottomY) / 2,
      y: (rackTopBottomY + rackBottomY) / 2,
    });
  }
  if (shape.type2BeamHeight > 0) {
    callouts.push({
      text: 'Beam',
      ax: leftX - halfT,
      ay: (rackBottomY + beamBottomY) / 2,
      y: (rackBottomY + beamBottomY) / 2,
    });
  }
  const partBounds = drawCallouts(parts, colX, callouts, partFont);

  const pad = dim * 1.4 + fontSize;
  setViewBox(
    svg,
    padBounds(
      { minX: xLeft - pad, minY: yTop - pad, maxX: xRight + pad, maxY: yBottom + dim + pad },
      partBounds,
      partFont * 0.6,
    ),
  );
}

/** Draw the active track-type cross-section into `svg` (metres stored, labels in mm). */
export function drawTrackShapeViewer(svg: SVGSVGElement, shape: TrackShapeSettings): void {
  svg.replaceChildren();
  ensureMarkers(svg);
  switch (shape.trackType) {
    case 'type1':
      drawType1(svg, shape);
      return;
    case 'type2':
      drawType2(svg, shape);
      return;
    default: {
      const _never: never = shape.trackType;
      throw new Error(`Unhandled track type: ${_never}`);
    }
  }
}
